import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PasswordFields } from "@/components/PasswordFields";
import { SocialTab } from "@/components/SocialTab";
import { ReviewsTab } from "@/components/ReviewsTab";
import { Users, DollarSign, CalendarClock, Package, Ban, CheckCircle2, Trash2, Plus, Pencil, AlertCircle, History, ShieldCheck, UserPlus, KeyRound, UserMinus, Banknote, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TIERS = ["free", "intermediate", "pro"];
const STATUS = { open: "bg-amber-100 text-amber-800", in_progress: "bg-blue-100 text-blue-800", resolved: "bg-green-100 text-green-800" };

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-border rounded-sm p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground"><Icon className="w-4 h-4" />{label}</div>
      <div className="font-display text-3xl font-semibold mt-3">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [reports, setReports] = useState(null);
  const [subs, setSubs] = useState([]);
  const [cats, setCats] = useState([]);
  const [issues, setIssues] = useState([]);
  const [logs, setLogs] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [feePercent, setFeePercent] = useState(null);
  // admins management (owner only)
  const [admins, setAdmins] = useState([]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [promoteEmail, setPromoteEmail] = useState("");
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwValid, setPwValid] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [custDetail, setCustDetail] = useState(null);
  const [custPwTarget, setCustPwTarget] = useState(null);
  const [custPwValue, setCustPwValue] = useState("");
  const [custPwValid, setCustPwValid] = useState(false);
  // category dialog
  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", icon: "Tag" });
  const [catEditId, setCatEditId] = useState(null);
  // issue dialog
  const [issueOpen, setIssueOpen] = useState(false);
  const [activeIssue, setActiveIssue] = useState(null);
  const [issueForm, setIssueForm] = useState({ status: "open", admin_response: "" });
  // founding provider dialog (owner only)
  const [foundingOpen, setFoundingOpen] = useState(false);
  const [foundingTarget, setFoundingTarget] = useState(null);
  const [foundingExpiry, setFoundingExpiry] = useState("");

  const loadAll = () => {
    api.get("/admin/reports").then((r) => setReports(r.data));
    api.get("/admin/subscribers").then((r) => setSubs(r.data));
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/admin/issues").then((r) => setIssues(r.data));
    api.get("/admin/audit-logs").then((r) => setLogs(r.data));
    api.get("/settings/password-policy").then((r) => setPolicy(r.data));
    api.get("/settings/platform-fee").then((r) => setFeePercent(r.data.percent));
    if (user?.is_owner) api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const setTier = async (id, tier) => {
    try { await api.put(`/admin/subscribers/${id}/tier`, { tier }); toast.success(`Tier set to ${tier}`); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const grantFounding = async () => {
    if (!foundingTarget) return;
    try {
      await api.post(`/admin/subscribers/${foundingTarget.id}/founding`, { expires_at: foundingExpiry || null });
      toast.success("Founding Provider granted");
      setFoundingOpen(false); setFoundingTarget(null); setFoundingExpiry(""); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const revokeFounding = async (id) => {
    if (!window.confirm("Revoke Founding Provider status? They'll revert to Basic (free) and lose Pro features.")) return;
    try { await api.delete(`/admin/subscribers/${id}/founding`); toast.success("Reverted to Basic"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/users/${id}/status`, { status }); toast.success(status === "suspended" ? "Subscriber suspended" : "Subscriber reactivated"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Subscriber removed"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCat = (c) => { if (c) { setCatForm({ name: c.name, icon: c.icon }); setCatEditId(c.id); } else { setCatForm({ name: "", icon: "Tag" }); setCatEditId(null); } setCatOpen(true); };
  const saveCat = async (e) => {
    e.preventDefault();
    try {
      if (catEditId) await api.put(`/admin/categories/${catEditId}`, catForm);
      else await api.post("/admin/categories", catForm);
      toast.success("Category saved"); setCatOpen(false); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delCat = async (id) => {
    try { await api.delete(`/admin/categories/${id}`); toast.success("Category deleted"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openIssue = (i) => { setActiveIssue(i); setIssueForm({ status: i.status, admin_response: i.admin_response || "" }); setIssueOpen(true); };  const saveIssue = async (e) => {
    e.preventDefault();
    try { await api.put(`/admin/issues/${activeIssue.id}`, issueForm); toast.success("Ticket updated"); setIssueOpen(false); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setPolicyField = (k, v) => setPolicy((p) => ({ ...p, [k]: v }));
  const savePolicy = async () => {
    try { const { data } = await api.put("/admin/settings/password-policy", policy); setPolicy(data); toast.success("Password policy updated"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveFee = async () => {
    try {
      const pct = Math.max(0, Math.min(100, Number(feePercent) || 0));
      const { data } = await api.put("/admin/settings/platform-fee", { percent: pct });
      setFeePercent(data.percent); toast.success(`Platform fee set to ${data.percent}%`); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // --- admin account management (owner only) ---
  const createAdmin = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/admins", adminForm); toast.success("Admin created"); setAdminOpen(false); setAdminForm({ name: "", email: "", password: "" }); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const promoteAdmin = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/admins/promote", { email: promoteEmail }); toast.success("User promoted to admin"); setPromoteEmail(""); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const demoteAdmin = async (id) => {
    try { await api.post(`/admin/admins/${id}/demote`); toast.success("Admin demoted to customer"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const removeAdmin = async (id) => {
    try { await api.delete(`/admin/admins/${id}`); toast.success("Admin removed"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setAdminStatus = async (id, status) => {
    try { await api.put(`/admin/admins/${id}/status`, { status }); toast.success(status === "suspended" ? "Admin suspended" : "Admin reactivated"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resetAdminPw = async () => {
    try { await api.post(`/admin/admins/${pwTarget.id}/reset-password`, { password: pwValue }); toast.success(`Password reset for ${pwTarget.email}`); setPwTarget(null); setPwValue(""); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const viewCustomer = async (id) => {
    try { const { data } = await api.get(`/admin/customers/${id}`); setCustDetail(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resetCustomerPw = async () => {
    try { await api.post(`/admin/customers/${custPwTarget.id}/reset-password`, { password: custPwValue }); toast.success(`Password reset for ${custPwTarget.email}`); setCustPwTarget(null); setCustPwValue(""); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!reports) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-4xl font-semibold tracking-tighter">System Admin</h1>
      <p className="text-muted-foreground mt-1">Signed in as {user.name} · full platform control.</p>

      <Tabs defaultValue="reports" className="mt-8">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="reports" className="rounded-sm" data-testid="tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="subscribers" className="rounded-sm" data-testid="tab-subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-sm" data-testid="tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="categories" className="rounded-sm" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-sm" data-testid="tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="issues" className="rounded-sm" data-testid="tab-issues">Issues{reports.open_issues > 0 && <span className="ml-1.5 text-xs bg-primary text-white rounded-sm px-1.5">{reports.open_issues}</span>}</TabsTrigger>
          <TabsTrigger value="audit" className="rounded-sm" data-testid="tab-audit">Audit Log</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-sm" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="social" className="rounded-sm" data-testid="tab-social">Social</TabsTrigger>
          {isOwner && <TabsTrigger value="admins" className="rounded-sm" data-testid="tab-admins">Admins</TabsTrigger>}
        </TabsList>

        {/* REPORTS */}
        <TabsContent value="reports" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Users} label="Total users" value={reports.total_users} />
            <Stat icon={DollarSign} label="Deposits collected" value={`$${reports.deposits_collected ?? 0}`} />
            <Stat icon={Banknote} label="Direct payments confirmed" value={`$${reports.direct_payments_confirmed ?? 0}`} />
            <Stat icon={CalendarClock} label="Total bookings" value={reports.total_bookings} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={DollarSign} label="Booking value (GMV)" value={`$${reports.total_revenue}`} />
            <Stat icon={Package} label="Active services" value={reports.active_services} />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white border border-border rounded-sm p-6">
              <h3 className="font-display text-lg font-medium">Subscribers by tier</h3>
              <div className="mt-4 space-y-2">
                {TIERS.map((t) => (
                  <div key={t} className="flex justify-between text-sm" data-testid={`report-tier-${t}`}>
                    <span className="capitalize text-muted-foreground">{t}</span>
                    <span className="font-semibold">{reports.subscribers_by_tier[t] || 0}</span>
                  </div>
                ))}
                {reports.subscribers_by_tier.founding > 0 && (
                  <div className="flex justify-between text-sm" data-testid="report-tier-founding">
                    <span className="text-amber-700 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" />Founding</span>
                    <span className="font-semibold">{reports.subscribers_by_tier.founding}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-2 border-t border-border">
                  <span className="text-muted-foreground">Customers</span><span className="font-semibold">{reports.total_customers}</span>
                </div>
              </div>
            </div>
            <div className="bg-white border border-border rounded-sm p-6">
              <h3 className="font-display text-lg font-medium">Bookings by status</h3>
              <div className="mt-4 space-y-2">
                {Object.entries(reports.bookings_by_status).map(([s, n]) => (
                  <div key={s} className="flex justify-between text-sm">
                    <span className="capitalize text-muted-foreground">{s}</span><span className="font-semibold">{n}</span>
                  </div>
                ))}
                {Object.keys(reports.bookings_by_status).length === 0 && <p className="text-sm text-muted-foreground">No bookings yet.</p>}
              </div>
            </div>
            <div className="bg-white border border-border rounded-sm p-6">
              <h3 className="font-display text-lg font-medium">Top services</h3>
              <div className="mt-4 space-y-2">
                {reports.top_services.map((s) => (
                  <div key={s.title} className="flex justify-between text-sm">
                    <span className="text-muted-foreground truncate pr-2">{s.title}</span><span className="font-semibold">{s.bookings}</span>
                  </div>
                ))}
                {reports.top_services.length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* SUBSCRIBERS */}
        <TabsContent value="subscribers" className="mt-6">
          {subs.length === 0 ? <p className="text-muted-foreground" data-testid="subs-empty">No subscribers yet.</p> : (
            <div className="bg-white border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="p-4">Name</th><th className="p-4">Tier</th><th className="p-4">Services</th><th className="p-4">Bookings</th><th className="p-4">Revenue</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`sub-row-${s.id}`}>
                      <td className="p-4"><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.email}</div></td>
                      <td className="p-4">
                        {s.tier === "founding" ? (
                          <div className="space-y-1">
                            <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 font-medium inline-flex items-center gap-1" data-testid={`founding-badge-${s.id}`}><Sparkles className="w-3 h-3" />Founding Provider</span>
                            <div className="text-[11px] text-muted-foreground" data-testid={`founding-expiry-${s.id}`}>{s.founding_expires_at ? `until ${new Date(s.founding_expires_at).toLocaleDateString()}` : "Lifetime"}</div>
                          </div>
                        ) : (
                          <Select value={s.tier || "free"} onValueChange={(v) => setTier(s.id, v)}>
                            <SelectTrigger className="w-32 h-8 rounded-sm" data-testid={`tier-select-${s.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      </td>
                      <td className="p-4">{s.service_count}</td>
                      <td className="p-4">{s.booking_count}</td>
                      <td className="p-4 font-medium text-primary">${s.revenue}</td>
                      <td className="p-4"><span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${s.status === "suspended" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>{s.status || "active"}</span></td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-end">
                          {isOwner && (s.tier === "founding" ? (
                            <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => revokeFounding(s.id)} data-testid={`revoke-founding-${s.id}`}>Revoke Founding</Button>
                          ) : (
                            <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => { setFoundingTarget(s); setFoundingExpiry(""); setFoundingOpen(true); }} data-testid={`grant-founding-${s.id}`}><Sparkles className="w-3.5 h-3.5 mr-1" />Founding</Button>
                          ))}
                          {s.status === "suspended" ? (
                            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setStatus(s.id, "active")} data-testid={`activate-${s.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></Button>
                          ) : (
                            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setStatus(s.id, "suspended")} data-testid={`suspend-${s.id}`}><Ban className="w-3.5 h-3.5" /></Button>
                          )}
                          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => delUser(s.id)} data-testid={`delete-sub-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Dialog open={foundingOpen} onOpenChange={setFoundingOpen}>
            <DialogContent data-testid="founding-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Grant Founding Provider</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Grants <span className="font-medium text-foreground">{foundingTarget?.name}</span> full Pro features with <span className="font-medium text-foreground">no monthly fee</span> (only the per-transaction platform fee applies). Use this to recruit early providers.
                </p>
                <div>
                  <Label className="text-sm">Expiry date <span className="text-muted-foreground font-normal">(leave blank for lifetime)</span></Label>
                  <Input type="date" value={foundingExpiry} onChange={(e) => setFoundingExpiry(e.target.value)} className="mt-1 rounded-sm" data-testid="founding-expiry-input" />
                  <p className="text-xs text-muted-foreground mt-1">On expiry they automatically revert to Basic (free).</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setFoundingOpen(false)}>Cancel</Button>
                <Button className="rounded-sm" onClick={grantFounding} data-testid="founding-grant-submit">Grant Founding Provider</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        <TabsContent value="categories" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">{cats.length} categories used across services</p>
            <Dialog open={catOpen} onOpenChange={setCatOpen}>
              <DialogTrigger asChild><Button className="rounded-sm" onClick={() => openCat(null)} data-testid="add-category-btn"><Plus className="w-4 h-4 mr-1" />New category</Button></DialogTrigger>
              <DialogContent className="max-w-sm rounded-sm">
                <DialogHeader><DialogTitle className="font-display text-xl">{catEditId ? "Edit" : "New"} category</DialogTitle></DialogHeader>
                <form onSubmit={saveCat} className="space-y-4">
                  <div><Label>Name</Label><Input value={catForm.name} onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1 rounded-sm" data-testid="category-name" /></div>
                  <DialogFooter><Button type="submit" className="rounded-sm w-full" data-testid="save-category-btn">Save</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cats.map((c) => (
              <div key={c.id} className="bg-white border border-border rounded-sm p-5 flex items-center justify-between" data-testid={`cat-${c.id}`}>
                <span className="font-medium">{c.name}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => openCat(c)} data-testid={`edit-cat-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => delCat(c.id)} data-testid={`delete-cat-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ISSUES */}
        <TabsContent value="issues" className="mt-6">
          {issues.length === 0 ? <p className="text-muted-foreground" data-testid="issues-admin-empty">No support tickets.</p> : (
            <div className="space-y-3">
              {issues.map((i) => (
                <div key={i.id} className="bg-white border border-border rounded-sm p-5" data-testid={`admin-issue-${i.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{i.subject}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${STATUS[i.status]}`}>{i.status.replace("_", " ")}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{i.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">from {i.user_name} ({i.user_role}) · {i.user_email}</p>
                      {i.admin_response && <p className="text-sm mt-2 border-l-2 border-primary pl-3">{i.admin_response}</p>}
                    </div>
                    <Button size="sm" variant="outline" className="rounded-sm shrink-0" onClick={() => openIssue(i)} data-testid={`manage-issue-${i.id}`}>Manage</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
            <DialogContent className="max-w-md rounded-sm">
              <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><AlertCircle className="w-5 h-5 text-primary" />Manage ticket</DialogTitle></DialogHeader>
              {activeIssue && (
                <form onSubmit={saveIssue} className="space-y-4">
                  <p className="text-sm font-medium">{activeIssue.subject}</p>
                  <div>
                    <Label>Status</Label>
                    <Select value={issueForm.status} onValueChange={(v) => setIssueForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="issue-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Response</Label><Textarea value={issueForm.admin_response} onChange={(e) => setIssueForm((f) => ({ ...f, admin_response: e.target.value }))} rows={3} className="mt-1 rounded-sm" data-testid="issue-response" /></div>
                  <DialogFooter><Button type="submit" className="rounded-sm w-full" data-testid="save-issue-btn">Update ticket</Button></DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* AUDIT LOG */}
        <TabsContent value="audit" className="mt-6">
          {logs.length === 0 ? <p className="text-muted-foreground" data-testid="audit-empty">No admin actions logged yet.</p> : (
            <div className="bg-white border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="p-4">When</th><th className="p-4">Admin</th><th className="p-4">Action</th><th className="p-4">Target</th><th className="p-4">Detail</th></tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-t border-border" data-testid={`audit-${l.id}`}>
                      <td className="p-4 text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="p-4 font-medium">{l.admin_name}</td>
                      <td className="p-4"><span className="text-xs px-2 py-0.5 rounded-sm bg-muted font-medium flex items-center gap-1 w-fit"><History className="w-3 h-3" />{l.action}</span></td>
                      <td className="p-4 text-muted-foreground">{l.target_type}</td>
                      <td className="p-4 text-muted-foreground">{l.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* SETTINGS — password policy */}
        <TabsContent value="settings" className="mt-6">
          <div className="bg-white border border-border rounded-sm p-6 max-w-lg">
            <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-medium">Password policy</h2></div>
            <p className="text-sm text-muted-foreground mt-1">Applies to new registrations and password resets.</p>
            {policy && (
              <div className="mt-5 space-y-4">
                <div>
                  <Label>Minimum length</Label>
                  <Input type="number" min="4" max="64" value={policy.min_length}
                    onChange={(e) => setPolicyField("min_length", Math.max(4, Math.min(64, Number(e.target.value) || 4)))}
                    className="mt-1 rounded-sm w-28" data-testid="policy-min-length" />
                </div>
                {[
                  ["require_uppercase", "Require an uppercase letter"],
                  ["require_lowercase", "Require a lowercase letter"],
                  ["require_number", "Require a number"],
                  ["require_symbol", "Require a symbol"],
                ].map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between border-t border-border pt-3">
                    <Label className="font-normal">{label}</Label>
                    <Switch checked={!!policy[k]} onCheckedChange={(v) => setPolicyField(k, v)} data-testid={`policy-${k}`} />
                  </div>
                ))}
                <Button onClick={savePolicy} className="rounded-sm w-full mt-2" data-testid="save-policy-btn">Save policy</Button>
              </div>
            )}
          </div>

          <div className="bg-white border border-border rounded-sm p-6 max-w-lg mt-6" data-testid="platform-fee-card">
            <div className="flex items-center gap-2"><Banknote className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-medium">Platform fee</h2></div>
            <p className="text-sm text-muted-foreground mt-1">The percentage BookdHUB keeps from each provider payout via Stripe Connect. Set 0 for no fee.</p>
            {feePercent !== null && (
              <div className="mt-5 space-y-3">
                <div>
                  <Label>Application fee (%)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" min="0" max="100" step="0.1" value={feePercent}
                      onChange={(e) => setFeePercent(e.target.value)}
                      className="rounded-sm w-28" data-testid="platform-fee-input" />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Example: on a $100 payout, BookdHUB keeps ${((Math.max(0, Math.min(100, Number(feePercent) || 0)) / 100) * 100).toFixed(2)} and the provider receives ${(100 - (Math.max(0, Math.min(100, Number(feePercent) || 0)) / 100) * 100).toFixed(2)}.</p>
                <Button onClick={saveFee} className="rounded-sm w-full mt-1" data-testid="save-fee-btn">Save platform fee</Button>
              </div>
            )}
          </div>
        </TabsContent>
        {/* ADMINS — owner-only account management */}
        {isOwner && (
        <TabsContent value="admins" className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-xl font-medium">Admin accounts</h2>
              <p className="text-sm text-muted-foreground">Only you (the owner) can add or manage admins. The owner account is protected and the last admin can't be removed.</p>
            </div>
            <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="add-admin-btn"><UserPlus className="w-4 h-4 mr-1.5" />Add admin</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md rounded-sm">
                <DialogHeader><DialogTitle className="font-display text-2xl">New admin</DialogTitle></DialogHeader>
                <form onSubmit={createAdmin} className="space-y-4">
                  <div><Label>Full name</Label><Input value={adminForm.name} onChange={(e) => setAdminForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1.5 rounded-sm" data-testid="admin-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={adminForm.email} onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))} required className="mt-1.5 rounded-sm" data-testid="admin-email" /></div>
                  <div><Label>Temporary password</Label><Input type="text" value={adminForm.password} onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))} required className="mt-1.5 rounded-sm" data-testid="admin-password" /><p className="text-xs text-muted-foreground mt-1">Must meet the password policy. Share it securely; they can change it after signing in.</p></div>
                  <DialogFooter><Button type="submit" className="rounded-sm w-full" data-testid="save-admin-btn">Create admin</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Promote existing user */}
          <form onSubmit={promoteAdmin} className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-end gap-3" data-testid="promote-form">
            <div className="flex-1 min-w-[220px]">
              <Label>Promote an existing user to admin</Label>
              <Input type="email" value={promoteEmail} onChange={(e) => setPromoteEmail(e.target.value)} placeholder="user@email.com" required className="mt-1.5 rounded-sm" data-testid="promote-email" />
            </div>
            <Button type="submit" variant="outline" className="rounded-sm" data-testid="promote-btn"><ShieldCheck className="w-4 h-4 mr-1.5" />Make admin</Button>
          </form>

          <div className="bg-white border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {admins.map((a) => {
                  const isSelf = a.id === user.id;
                  return (
                    <tr key={a.id} className="border-t border-border" data-testid={`admin-row-${a.id}`}>
                      <td className="p-4 font-medium">{a.name}{isSelf && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}</td>
                      <td className="p-4 text-muted-foreground">{a.email}</td>
                      <td className="p-4">{a.is_owner ? <span className="text-xs px-2 py-0.5 rounded-sm bg-primary/10 text-primary font-medium">Owner</span> : <span className="text-xs px-2 py-0.5 rounded-sm bg-neutral-200 text-neutral-700 font-medium">Admin</span>}</td>
                      <td className="p-4"><span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${a.status === "suspended" ? "bg-neutral-200 text-neutral-600" : "bg-green-100 text-green-800"}`}>{a.status || "active"}</span></td>
                      <td className="p-4">
                        {a.is_owner || isSelf ? (
                          <span className="text-xs text-muted-foreground block text-right">Protected</span>
                        ) : (
                          <div className="flex gap-2 justify-end flex-wrap">
                            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setPwTarget(a)} data-testid={`reset-pw-${a.id}`}><KeyRound className="w-3.5 h-3.5 mr-1" />Reset PW</Button>
                            {a.status === "suspended"
                              ? <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setAdminStatus(a.id, "active")} data-testid={`reactivate-admin-${a.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Reactivate</Button>
                              : <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setAdminStatus(a.id, "suspended")} data-testid={`suspend-admin-${a.id}`}><Ban className="w-3.5 h-3.5 mr-1" />Suspend</Button>}
                            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => demoteAdmin(a.id)} data-testid={`demote-admin-${a.id}`}><UserMinus className="w-3.5 h-3.5 mr-1" />Demote</Button>
                            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => removeAdmin(a.id)} data-testid={`remove-admin-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Reset password dialog */}
          <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setPwValue(""); setPwValid(false); } }}>
            <DialogContent className="max-w-sm rounded-sm">
              <DialogHeader><DialogTitle className="font-display text-2xl">Reset password</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground mb-2">Set a new password for <b>{pwTarget?.email}</b>.</p>
              <PasswordFields key={pwTarget?.id} onChange={(pw, v) => { setPwValue(pw); setPwValid(v); }} autoFocus />
              <DialogFooter><Button className="rounded-sm w-full" onClick={resetAdminPw} disabled={!pwValid} data-testid="reset-pw-confirm">Reset password</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* CUSTOMERS */}
        <TabsContent value="customers" className="mt-6">
          {customers.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-12 text-center" data-testid="customers-empty">
              <p className="text-muted-foreground">No customers registered yet.</p>
            </div>
          ) : (
            <div className="bg-white border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="p-4">Customer</th><th className="p-4">Type</th><th className="p-4">Bookings</th><th className="p-4">Spent</th><th className="p-4">Joined</th><th className="p-4 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.id} className="border-t border-border" data-testid={`customer-row-${c.id}`}>
                      <td className="p-4">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </td>
                      <td className="p-4">
                        {c.customer_type === "business" ? (
                          <span className="text-xs px-2 py-0.5 rounded-sm bg-primary/10 text-primary font-medium">Business{c.org_name ? ` · ${c.org_name}` : ""}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Individual</span>
                        )}
                      </td>
                      <td className="p-4">{c.booking_count}{c.active_subscriptions > 0 && <span className="ml-1.5 text-[10px] uppercase font-bold text-primary bg-primary/10 rounded-sm px-1.5 py-0.5">{c.active_subscriptions} recurring</span>}</td>
                      <td className="p-4 font-medium text-primary">${c.total_spent}</td>
                      <td className="p-4 text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => viewCustomer(c.id)} data-testid={`view-customer-${c.id}`}>View</Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => { setCustPwTarget(c); setCustPwValue(""); }} data-testid={`reset-customer-pw-${c.id}`}><KeyRound className="w-3.5 h-3.5 mr-1" />Reset password</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="social" className="mt-6">
          <SocialTab subscribers={subs} />
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <ReviewsTab />
        </TabsContent>
      </Tabs>

      {/* Customer detail dialog */}
      <Dialog open={!!custDetail} onOpenChange={(o) => { if (!o) setCustDetail(null); }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="customer-detail-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{custDetail?.customer?.name}</DialogTitle></DialogHeader>
          {custDetail && (
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Demographics</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <div><span className="text-muted-foreground">Email:</span> {custDetail.customer.email}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {custDetail.customer.phone || "—"}</div>
                  <div><span className="text-muted-foreground">Type:</span> {custDetail.customer.customer_type === "business" ? "Business" : "Individual"}</div>
                  <div><span className="text-muted-foreground">SMS consent:</span> {custDetail.customer.sms_consent ? "Yes" : "No"}</div>
                  <div><span className="text-muted-foreground">Joined:</span> {custDetail.customer.created_at ? new Date(custDetail.customer.created_at).toLocaleDateString() : "—"}</div>
                  <div><span className="text-muted-foreground">Status:</span> {custDetail.customer.status || "active"}</div>
                  {custDetail.customer.customer_type === "business" && <>
                    <div><span className="text-muted-foreground">Organization:</span> {custDetail.customer.org_name || "—"}</div>
                    <div><span className="text-muted-foreground">Role/title:</span> {custDetail.customer.org_title || "—"}</div>
                    <div><span className="text-muted-foreground">Business email:</span> {custDetail.customer.org_email || "—"}</div>
                    <div><span className="text-muted-foreground">Tax ID/EIN:</span> {custDetail.customer.org_tax_id || "—"}</div>
                  </>}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Service activity</p>
                <div className="grid grid-cols-4 gap-3">
                  {[["Bookings", custDetail.stats.total_bookings], ["Completed", custDetail.stats.completed], ["Recurring", custDetail.stats.active_subscriptions], ["Spent", `$${custDetail.stats.total_spent}`]].map(([l, v]) => (
                    <div key={l} className="border border-border rounded-sm p-3"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</div><div className="font-display text-lg font-semibold">{v}</div></div>
                  ))}
                </div>
                {custDetail.bookings.length > 0 && (
                  <div className="mt-4 border border-border rounded-sm divide-y divide-border max-h-56 overflow-y-auto" data-testid="customer-bookings">
                    {custDetail.bookings.map((b) => (
                      <div key={b.id} className="flex items-center justify-between p-3 text-sm">
                        <div><span className="font-medium">{b.service_title}</span><div className="text-xs text-muted-foreground">{b.provider_name} · {b.date} {b.start_time}{b.is_recurring_visit ? " · recurring" : ""}</div></div>
                        <div className="text-right"><div className="font-medium text-primary">${b.price}</div><div className="text-xs text-muted-foreground capitalize">{b.status} · {b.payment_status}</div></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="outline" className="rounded-sm" onClick={() => { const c = custDetail.customer; setCustDetail(null); setCustPwTarget(c); setCustPwValue(""); }} data-testid="detail-reset-pw"><KeyRound className="w-4 h-4 mr-1.5" />Reset password</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer reset password dialog */}
      <Dialog open={!!custPwTarget} onOpenChange={(o) => { if (!o) { setCustPwTarget(null); setCustPwValue(""); setCustPwValid(false); } }}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="customer-pw-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reset password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">Set a new password for <b>{custPwTarget?.email}</b>.</p>
          <PasswordFields key={custPwTarget?.id} onChange={(pw, v) => { setCustPwValue(pw); setCustPwValid(v); }} autoFocus />
          <DialogFooter><Button className="rounded-sm w-full" onClick={resetCustomerPw} disabled={!custPwValid} data-testid="customer-pw-confirm">Reset password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
